import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import Swal from 'sweetalert2';
import { setIdTransaccion } from '../../../actions';
import { API_URL } from '../../../services/Api';
import { getRifasDisponibles, validarNumeroRifa } from '../../../services/Rifa';
import { venta_rifa } from '../../../services/Venta';
import { alerta, cerrarModal, formatearMonedaValue, toast } from '../../../services/utilities';

function VentaRifas(props){
    const [step, setStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [rifas, setRifas] = useState([]);
    const [rifaSeleccionada, setRifaSeleccionada] = useState(null);
    const [idTransaccion, setIdTransaccionLocal] = useState(0);
    const [showImage, setShowImage] = useState(false);
    const [form, setForm] = useState({
        nombre: '',
        celular: '',
        numero: '',
        numeros: []
    });

    useEffect(() => {
        handleGetRifasDisponibles();
    }, []);

    const resetModal = () => {
        setStep(0);
        setLoading(false);
        setRifaSeleccionada(null);
        setIdTransaccionLocal(0);
        setShowImage(false);
        setForm({
            nombre: '',
            celular: '',
            numero: '',
            numeros: []
        });
    };

    const handleGetRifasDisponibles = async () => {
        setLoading(true);
        try{
            const response = await getRifasDisponibles();
            if(response.status === 200){
                setRifas(response.data.rifas ?? []);
            }else{
                alerta('No se cargaron las rifas disponibles', response.response?.data?.status_message ?? response.message, 'warning');
            }
        }catch(error){
            alerta('Error al cargar rifas', 'Por favor intente nuevamente.', 'error');
        }
        setLoading(false);
    };

    const handleSeleccionarRifa = (rifa) => {
        setRifaSeleccionada(rifa);
        setForm({
            nombre: '',
            celular: '',
            numero: '',
            numeros: []
        });
        setShowImage(false);
        setStep(1);
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        if(name === 'celular'){
            setForm(prev => ({ ...prev, celular: value.replace(/\D/g, '').slice(0, 10) }));
            return;
        }

        if(name === 'numero'){
            setForm(prev => ({ ...prev, numero: value.replace(/\D/g, '').slice(0, rifaSeleccionada?.cantidad_cifras ?? 4) }));
            return;
        }

        setForm(prev => ({ ...prev, [name]: value }));
    };

    const totalVenta = useMemo(() => {
        const valor = parseFloat(rifaSeleccionada?.valor_venta ?? 0) || 0;
        return valor * form.numeros.length;
    }, [form.numeros.length, rifaSeleccionada]);

    const formatFechaRifa = (fecha) => {
        if(!fecha){
            return '';
        }

        const date = new Date(fecha.replace(' ', 'T'));
        return date.toLocaleDateString('es-CO', {
            weekday: 'long',
            day: '2-digit',
            month: 'long'
        }).toUpperCase();
    };

    const estadoRifa = (estado_id) => estado_id === 8 ? 'Habilitada' : 'No disponible';

    const getRifaImageUrl = (img) => {
        if(!img){
            return '';
        }

        return `${API_URL}public/imagenes/rifa/${img}`;
    };

    const handleAgregarNumero = async () => {
        if(!rifaSeleccionada){
            alerta('Seleccione una rifa', 'Debe elegir una rifa válida');
            return;
        }

        if(form.numero.length !== Number(rifaSeleccionada.cantidad_cifras)){
            alerta('Número inválido', `Debe ingresar un número de ${rifaSeleccionada.cantidad_cifras} cifras`);
            return;
        }

        if(form.numeros.includes(form.numero)){
            alerta('Número repetido', 'El número ya fue agregado a la lista');
            return;
        }

        setLoading(true);
        try{
            const response = await validarNumeroRifa(rifaSeleccionada.id_rifa, form.numero);
            if(response.status === 200 && response.data.status_code === 200){
                setForm(prev => ({
                    ...prev,
                    numeros: [...prev.numeros, prev.numero],
                    numero: ''
                }));
            }else{
                alerta('Número no disponible', response.data?.status_message ?? response.response?.data?.status_message ?? 'No se pudo validar el número');
            }
        }catch(error){
            alerta('Error al validar el número', 'Por favor intente nuevamente');
        }
        setLoading(false);
    };

    const handleEliminarNumero = (numero) => {
        setForm(prev => ({
            ...prev,
            numeros: prev.numeros.filter(item => item !== numero)
        }));
    };

    const handleConfirmarVenta = () => {
        if(!rifaSeleccionada){
            alerta('Seleccione una rifa', 'Debe elegir una rifa válida');
            return;
        }
        if(form.nombre.trim() === ''){
            alerta('Nombre requerido', 'Debe ingresar el nombre del cliente');
            return;
        }
        if(form.celular.length !== 10){
            alerta('Celular inválido', 'Debe ingresar un celular de 10 dígitos');
            return;
        }
        if(form.numeros.length === 0){
            alerta('Sin números', 'Debe agregar al menos un número a la venta');
            return;
        }

        Swal.fire({
            title: '¿Confirmar venta de rifa?',
            text: `Se venderán ${form.numeros.length} número(s) por $${formatearMonedaValue(totalVenta)}`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Confirmar',
            cancelButtonText: 'Cancelar'
        }).then((result) => {
            if(result.isConfirmed){
                handleRealizarVenta();
            }
        });
    };

    const handleRealizarVenta = async () => {
        setLoading(true);
        const payload = {
            id_rifa: rifaSeleccionada.id_rifa,
            nombre: form.nombre,
            celular: form.celular,
            numeros: form.numeros
        };

        try{
            const response = await venta_rifa(payload);
            if(response.status === 201){
                setIdTransaccionLocal(response.data.id_transaccion);
                props.setIdTransaccion(response.data.id_transaccion);
                setStep(2);
                toast('¡ Perfecto !', `Venta realizada ${response.data.id_transaccion}`, 'success');
            }else{
                alerta('No fue posible realizar la venta', response.data?.status_message ?? response.response?.data?.status_message ?? response.message);
            }
        }catch(error){
            alerta('Error al realizar la venta', 'Por favor intente nuevamente');
        }
        setLoading(false);
    };

    const handleAbrirUltimaVenta = () => {
        props.setIdTransaccion(idTransaccion);
        cerrarModal('ModalVentaRifas');
        setTimeout(() => {
            const detalleVentaTrigger = document.getElementById('btnAbrirDetalleVentaRifa');
            if(detalleVentaTrigger){
                detalleVentaTrigger.click();
            }
            resetModal();
        }, 150);
    };

    const handleFinalizar = () => {
        cerrarModal('ModalVentaRifas');
        resetModal();
    };

    return(
        <React.Fragment>
            <div className="modal fade" id="ModalVentaRifas" data-bs-backdrop="static" data-bs-keyboard="false" tabIndex="-1" aria-labelledby="ModalVentaRifas" aria-hidden="true">
                <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h1 className="modal-title fs-5">Venta de rifas</h1>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={resetModal}></button>
                        </div>
                        <div className="modal-body">
                            {loading ?
                                <div className="d-flex justify-content-center align-items-center" style={{height:'220px'}}>
                                    <div className="spinner-border" role="status"></div>
                                    <h4 className='mb-0 mx-2'>Procesando...</h4>
                                </div>
                            : null}

                            {!loading && step === 0 ? (
                                <div className='row'>
                                    {Array.isArray(rifas) && rifas.length > 0 ? rifas.map((rifa) => (
                                        <div className='col-md-6 mb-3' key={rifa.id_rifa}>
                                            <div className='card h-100 shadow-sm border-0'>
                                                {showImage && rifaSeleccionada?.id_rifa === rifa.id_rifa && rifa.img ?
                                                    <img
                                                        src={getRifaImageUrl(rifa.img)}
                                                        alt={rifa.descripcion}
                                                        className='card-img-top'
                                                        style={{objectFit:'cover', maxHeight:'220px'}}
                                                    />
                                                : null}
                                                <div className='card-body'>
                                                    <h4 className='text-center mb-2'><b>{rifa.descripcion}</b></h4>
                                                    <p className='text-center text-secondary mb-2'><i className="bi bi-calendar-event"></i> {formatFechaRifa(rifa.fecha_termina)}</p>
                                                    <div className='d-flex justify-content-center gap-2 flex-wrap mb-3'>
                                                        <span className='badge text-bg-primary'>{rifa.loteria} | {rifa.cantidad_cifras} cifras</span>
                                                        <span className={`badge ${rifa.estado_id === 8 ? 'text-bg-success' : 'text-bg-secondary'}`}>{estadoRifa(rifa.estado_id)}</span>
                                                    </div>
                                                    <h2 className='text-center mb-3'><b>Valor: ${formatearMonedaValue(rifa.valor_venta)}</b></h2>
                                                    {rifa.img ?
                                                        <div className='text-center mb-3'>
                                                            <button
                                                                type='button'
                                                                className='btn btn-light border'
                                                                onClick={() => {
                                                                    setRifaSeleccionada(rifa);
                                                                    setShowImage(prev => !prev);
                                                                }}
                                                            >
                                                                <i className="bi bi-image"></i> Ver imagen <i className="bi bi-chevron-down"></i>
                                                            </button>
                                                        </div>
                                                    : null}
                                                    <div className='text-center'>
                                                        <button
                                                            type='button'
                                                            className='btn btn-dark'
                                                            disabled={rifa.estado_id !== 8}
                                                            onClick={() => handleSeleccionarRifa(rifa)}
                                                        >
                                                            Seleccionar
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )) : (
                                        <div className='text-center text-secondary my-4'>
                                            <h4>No hay rifas disponibles</h4>
                                        </div>
                                    )}
                                </div>
                            ) : null}

                            {!loading && step === 1 && rifaSeleccionada ? (
                                <div className='row'>
                                    <div className='col-md-12'>
                                        <div className='card border-0 shadow-sm mb-3'>
                                            <div className='card-body text-center'>
                                                <h2><b>Valor: ${formatearMonedaValue(rifaSeleccionada.valor_venta)}</b></h2>
                                                <span className='badge text-bg-primary fs-6'>{rifaSeleccionada.loteria} | {rifaSeleccionada.cantidad_cifras} CIFRAS</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className='col-md-6 mb-3'>
                                        <div className="form-floating">
                                            <input type="text" className="form-control" id="nombre_rifa_cliente" name="nombre" value={form.nombre} onChange={handleInputChange} />
                                            <label htmlFor="nombre_rifa_cliente">Nombre</label>
                                        </div>
                                    </div>
                                    <div className='col-md-6 mb-3'>
                                        <div className="form-floating">
                                            <input type="text" className="form-control" id="celular_rifa_cliente" name="celular" value={form.celular} onChange={handleInputChange} />
                                            <label htmlFor="celular_rifa_cliente">Celular</label>
                                        </div>
                                    </div>
                                    <div className='col-md-8 mb-3'>
                                        <div className="form-floating">
                                            <input type="text" className="form-control text-center fs-4" id="numero_rifa" name="numero" value={form.numero} onChange={handleInputChange} />
                                            <label htmlFor="numero_rifa">Número</label>
                                        </div>
                                    </div>
                                    <div className='col-md-4 mb-3 d-flex align-items-center'>
                                        <button type='button' className='btn btn-outline-success w-100 rounded-pill py-3' onClick={handleAgregarNumero}>Agregar</button>
                                    </div>
                                    <div className='col-md-12'>
                                        <div className='card border-0 shadow-sm'>
                                            <div className='card-body'>
                                                {form.numeros.length > 0 ? form.numeros.map((numero, index) => (
                                                    <div key={index} className='d-flex justify-content-between align-items-center border-bottom py-2'>
                                                        <span className='fs-4'>{numero}</span>
                                                        <button type='button' className='btn btn-link text-danger p-0' onClick={() => handleEliminarNumero(numero)}>
                                                            <i className="bi bi-trash fs-4"></i>
                                                        </button>
                                                    </div>
                                                )) : (
                                                    <div className='text-center text-secondary py-4'>Agrega uno o más números para esta rifa.</div>
                                                )}
                                                <div className='text-center mt-4'>
                                                    <h2>Total: {formatearMonedaValue(totalVenta)}</h2>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : null}

                            {!loading && step === 2 ? (
                                <div className='text-center py-4'>
                                    <i className="bi bi-check-circle text-success" style={{fontSize:'96px'}}></i>
                                    <h2 className='mt-3 text-secondary'>Venta Realizada de Forma Exitosa</h2>
                                    <div className='mt-4'>
                                        <button type='button' className='btn btn-primary rounded-pill px-4 me-2' onClick={handleAbrirUltimaVenta}>
                                            Ultima venta #{idTransaccion}
                                        </button>
                                    </div>
                                    <div className='mt-3'>
                                        <button type='button' className='btn btn-dark rounded-pill px-4' onClick={handleFinalizar}>
                                            Finalizar
                                        </button>
                                    </div>
                                    <p className='text-secondary mt-4 mb-0'>
                                        Para ver mas información de esta venta vaya a el menu principal y seleccione ultima venta
                                    </p>
                                </div>
                            ) : null}
                        </div>
                        <div className="modal-footer">
                            {step === 1 ? (
                                <div className='d-flex justify-content-between w-100'>
                                    <button type='button' className='btn btn-outline-secondary rounded-pill px-4' onClick={() => setStep(0)}>
                                        <i className="bi bi-arrow-left"></i> Atras
                                    </button>
                                    <button type='button' className='btn btn-dark rounded-pill px-4' disabled={form.numeros.length === 0} onClick={handleConfirmarVenta}>
                                        Realizar venta
                                    </button>
                                </div>
                            ) : step === 0 ? (
                                <button type='button' className='btn btn-secondary' data-bs-dismiss='modal' onClick={resetModal}>Cerrar</button>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
            <button
                type='button'
                id='btnAbrirDetalleVentaRifa'
                className='d-none'
                data-bs-toggle='modal'
                data-bs-target='#modalDetalleVenta'
            >
                detalle
            </button>
        </React.Fragment>
    );
}

const mapDispatchToProps = {
    setIdTransaccion
};

export default connect(null, mapDispatchToProps)(VentaRifas);
